
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
  Switch,
} from '@chakra-ui/react'
import apiClient from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const languageSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
  code: z.string().min(1, 'Code is required').max(10, 'Code must be less than 10 characters'),
  native_name: z.string().optional(),
  flag: z.string().optional(),
  is_default: z.boolean().optional(),
  is_active: z.boolean().optional(),
  sort_order: z.number().int().min(0).optional(),
})

type LanguageFormData = z.infer<typeof languageSchema>

interface LanguageFormProps {
  languageId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function LanguageForm({ languageId, onSuccess, onCancel }: LanguageFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!languageId

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<LanguageFormData>({
    resolver: zodResolver(languageSchema),
    defaultValues: {
      name: '',
      code: '',
      native_name: '',
      flag: '',
      is_default: false,
      is_active: true,
      sort_order: 0,
    },
  })

  // Fetch language data for edit mode
  useEffect(() => {
    if (isEditMode && languageId) {
      setIsLoading(true)
      apiClient
        .get(`/system-settings/language/languages/${languageId}`)
        .then(({ data }) => {
          const languageData = data?.data || data || {}

          // Reset form with all values
          reset({
            name: languageData.name || '',
            code: languageData.code || '',
            native_name: languageData.native_name || '',
            flag: languageData.flag || '',
            is_default: languageData.is_default || false,
            is_active: languageData.is_active !== undefined ? languageData.is_active : true,
            sort_order: languageData.sort_order || 0,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isEditMode, languageId, reset, showError])

  const onSubmit = async (data: LanguageFormData) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: data.name,
        code: data.code,
        native_name: data.native_name || null,
        flag: data.flag || null,
        is_default: data.is_default || false,
        is_active: data.is_active !== undefined ? data.is_active : true,
        sort_order: data.sort_order || 0,
      }

      if (isEditMode) {
        await apiClient.put(`/system-settings/language/languages/${languageId}`, payload)
        showSuccess('Success', 'Language updated successfully')
      } else {
        await apiClient.post('/system-settings/language/languages', payload)
        showSuccess('Success', 'Language created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        setTimeout(() => navigate('/system-settings/language/languages'), 2000)
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/system-settings/language/languages')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading language...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. English, Spanish, French"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The display name of the language.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Code Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input
              {...register('code')}
              placeholder="e.g. en, es, fr"
              maxLength={10}
            />
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
            <FormHelperText>ISO language code (e.g., en, es, fr). Must be unique.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Native Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Native Name</FormLabel>
          <FormControl isInvalid={!!errors.native_name}>
            <Input
              {...register('native_name')}
              placeholder="e.g. English, Español, Français"
            />
            <FormErrorMessage>{errors.native_name?.message}</FormErrorMessage>
            <FormHelperText>The name of the language in its own language.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Flag Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Flag</FormLabel>
          <FormControl isInvalid={!!errors.flag}>
            <Input
              {...register('flag')}
              placeholder="e.g. 🇺🇸, 🇪🇸, 🇫🇷"
              maxLength={10}
            />
            <FormErrorMessage>{errors.flag?.message}</FormErrorMessage>
            <FormHelperText>Flag emoji or icon code (optional).</FormHelperText>
          </FormControl>
        </Grid>

        {/* Sort Order Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Sort Order</FormLabel>
          <FormControl isInvalid={!!errors.sort_order}>
            <Input
              type="number"
              {...register('sort_order', { valueAsNumber: true })}
              placeholder="0"
              min={0}
            />
            <FormErrorMessage>{errors.sort_order?.message}</FormErrorMessage>
            <FormHelperText>Display order (lower numbers appear first).</FormHelperText>
          </FormControl>
        </Grid>

        {/* Is Default Switch */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Default Language</FormLabel>
          <FormControl>
            <Controller
              name="is_default"
              control={control}
              render={({ field }) => (
                <Switch
                  isChecked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                  colorScheme="blue"
                />
              )}
            />
            <FormHelperText>Set this as the default language for the system.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Is Active Switch */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <Switch
                  isChecked={field.value !== undefined ? field.value : true}
                  onChange={(e) => field.onChange(e.target.checked)}
                  colorScheme="green"
                />
              )}
            />
            <FormHelperText>Enable or disable this language.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={handleCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Language'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

